/**
 * components/client/SectionTransition.tsx
 *
 * Lightweight transitional component displayed between field sections.
 * Shows a conversational message and a continue button.
 *
 * Updated June 2026: Removed ClientBrandingHeader from inside the card
 * (branding is already visible in the page header above) and removed the
 * "X elements dans cette section" count badge (unnecessary noise for the client).
 * Props logoUrl and senderDisplayName have been removed.
 */

import { ArrowRight } from 'lucide-react';
import { SECTION_METADATA } from '../../types/needs';

interface SectionTransitionProps {
  sectionKey: string;
  conversationalIntro?: string;
  onContinue: () => void;
}

export default function SectionTransition({
  sectionKey,
  conversationalIntro,
  onContinue,
}: SectionTransitionProps) {
  const sectionMeta = SECTION_METADATA[sectionKey] || SECTION_METADATA.informations;

  const getEmoji = () => {
    switch (sectionMeta.icon) {
      case 'shield':
        return '🛡️';
      case 'trending-up':
        return '📈';
      case 'home':
        return '🏠';
      case 'wallet':
        return '💰';
      case 'users':
        return '👥';
      case 'graduation-cap':
        return '🎓';
      default:
        return '📄';
    }
  };

  const defaultIntro = conversationalIntro || sectionMeta.conversational_transition;

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-xl animate-section-enter">
        <div className="bg-white rounded-3xl shadow-soft-xl p-8 md:p-12 text-center">

          {/* Section icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-3xl mb-6">
            <span className="text-4xl">{getEmoji()}</span>
          </div>

          {/* Section title */}
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-3">
            {sectionMeta.label}
          </h2>

          {/* Conversational intro */}
          <p className="text-lg text-neutral-700 leading-relaxed mb-8">
            {defaultIntro}
          </p>

          {/* Continue button */}
          <button
            onClick={onContinue}
            className="w-full px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 transition-colors shadow-soft flex items-center justify-center gap-2"
          >
            Continuer
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
